/**
 * Section-scoped Recharge enhancements for [data-hz-recharge-host] (main/arMainProduct).
 * - Ensures .recharge-supply-panel wraps app block + buy row when Liquid misses (editor / delayed apps).
 * - Restores quantity input inside quantity-counter if the app reparents it on mount.
 * - Syncs primary ATC label with hz-recharge-custom-selector display price.
 */
(function () {
  'use strict';

  var HOST_SEL = '[data-hz-recharge-host]';

  function repairQuantityInputPlacement(panel) {
    if (!panel) return;
    var form = panel.querySelector('form.wt-product__add-to-cart_form');
    if (!form) return;
    var qc = panel.querySelector('quantity-counter');
    var qty = form.querySelector('input.js-counter-quantity[name="quantity"]');
    if (!qc || !qty) return;
    if (qc.contains(qty)) return;

    var increment = qc.querySelector('.counter__increment');
    var increase = qc.querySelector('.js-counter-increase');
    if (!increment || !increase) return;
    increment.insertBefore(qty, increase);
  }

  function bindQuantityInputRepairForPanel(panel) {
    if (!panel) return;
    var form = panel.querySelector('form.wt-product__add-to-cart_form');
    if (!form || form.dataset.hzQtyRepairBound === '1') return;
    form.dataset.hzQtyRepairBound = '1';

    var t;
    function scheduleRepair() {
      clearTimeout(t);
      t = setTimeout(function () {
        repairQuantityInputPlacement(panel);
      }, 0);
    }

    scheduleRepair();
    var mo = new MutationObserver(scheduleRepair);
    mo.observe(form, { childList: true, subtree: true });
  }

  function ensureSupplyPanelWithin(host) {
    host.querySelectorAll('.shopify-block.recharge-subscription-widget').forEach(function (block) {
      if (block.closest('.recharge-supply-panel')) return;

      var panel = document.createElement('div');
      panel.className = 'recharge-supply-panel';
      var parent = block.parentElement;
      if (!parent) return;

      parent.insertBefore(panel, block);
      panel.appendChild(block);

      var sibling = panel.nextElementSibling;
      while (sibling) {
        if (sibling.classList && sibling.classList.contains('main-product__buy-buttons--container')) {
          panel.appendChild(sibling);
          break;
        }
        sibling = sibling.nextElementSibling;
      }
    });
  }

  function normalizePriceText(s) {
    return String(s || '')
      .replace(/\s+/g, ' ')
      .trim();
  }

  function stripTrailingPipePrice(s) {
    return normalizePriceText(String(s || '').replace(/\s*[|｜]\s*[^\s].*$/, ''));
  }

  function renderAtcLabel(span, baseLabel, priceText) {
    span.textContent = '';
    span.className = 'hz-atc-label-inner';

    var verb = document.createElement('span');
    verb.className = 'hz-atc-label-inner__verb';
    verb.textContent = baseLabel || '';

    span.appendChild(verb);

    if (!priceText) return;

    var sep = document.createElement('span');
    sep.className = 'hz-atc-label-inner__sep';
    sep.setAttribute('aria-hidden', 'true');

    var priceEl = document.createElement('span');
    priceEl.className = 'hz-atc-label-inner__price';
    priceEl.textContent = priceText;

    span.appendChild(sep);
    span.appendChild(priceEl);
  }

  function primaryAddToCartButton(host) {
    var panel = host.querySelector('.recharge-supply-panel');
    if (!panel) return null;
    return panel.querySelector('.product-form__submit.js-add-to-cart');
  }

  function customSelectorDisplayPrice(host) {
    var sel = host.querySelector('hz-recharge-custom-selector');
    if (!sel) return '';
    var price = normalizePriceText(sel.dataset.hzRcDisplayPrice || '');
    if (price) return price;
    if (sel.querySelector('[data-hz-rc-body][hidden]')) return '';
    return '';
  }

  function customSelectorStickyMeta(host) {
    var sel = host.querySelector('hz-recharge-custom-selector');
    if (!sel) return { subtitle: '', price: '', comparePrice: '' };
    return {
      subtitle: normalizePriceText(sel.dataset.hzRcDisplaySubtitle || ''),
      price: normalizePriceText(sel.dataset.hzRcDisplaySupplyPrice || ''),
      comparePrice: normalizePriceText(sel.dataset.hzRcDisplaySupplyComparePrice || ''),
    };
  }

  function syncAddToCartLabel(host, button) {
    var btn = button || primaryAddToCartButton(host);
    if (!btn) return;

    var span = btn.querySelector(':scope > span');
    if (!span) return;

    if (btn.dataset.hzAtcBase === undefined) {
      btn.dataset.hzAtcBase = stripTrailingPipePrice(span.textContent);
    }

    var base = btn.dataset.hzAtcBase;
    var inStickyBar = !!btn.closest('.hz-atc-sticky-bar');

    if (btn.disabled || btn.hasAttribute('disabled')) {
      renderAtcLabel(span, base, '');
      return;
    }

    renderAtcLabel(
      span,
      base,
      inStickyBar ? '' : customSelectorDisplayPrice(host) || ''
    );
  }

  function syncStickyAtcMeta(bodyBar, bodyMeta, bodyDetailCopy, bodyCompare, bodyPrice, host) {
    if (!bodyBar || !bodyMeta) return;
    var meta = customSelectorStickyMeta(host);
    var showMeta = bodyBar.classList.contains('hz-atc-sticky-bar--active') &&
      !!(meta.subtitle || meta.price);
    bodyMeta.hidden = !showMeta;
    bodyDetailCopy.textContent = meta.subtitle || '';

    var showCompare = !!(meta.comparePrice && meta.comparePrice !== meta.price);
    bodyCompare.textContent = showCompare ? meta.comparePrice : '';
    bodyCompare.hidden = !showCompare;
    bodyPrice.textContent = meta.price || '';
  }

  function stickyHeaderOffset() {
    var header = document.querySelector('.page-header');
    if (!header) return 0;
    return Math.max(0, Math.round(header.getBoundingClientRect().bottom));
  }

  function stickySoonerOffset() {
    return window.matchMedia('(max-width: 899px)').matches ? 0 : 56;
  }

  function bindStickyAtcForPanel(panel) {
    var container = panel.querySelector('.main-product__buy-buttons--container');
    if (!container || container.dataset.hzStickyAtcBound === '1') return;
    container.dataset.hzStickyAtcBound = '1';

    var sentinel = document.createElement('div');
    sentinel.className = 'hz-atc-sticky-sentinel';
    sentinel.setAttribute('aria-hidden', 'true');
    container.parentNode.insertBefore(sentinel, container);

    var placeholder = document.createElement('div');
    placeholder.className = 'hz-atc-sticky-placeholder';
    placeholder.setAttribute('aria-hidden', 'true');
    placeholder.hidden = true;
    container.insertAdjacentElement('beforebegin', placeholder);

    var bodyBar = document.createElement('div');
    bodyBar.className = 'hz-atc-sticky-bar';
    document.body.appendChild(bodyBar);

    var bodyMeta = document.createElement('div');
    bodyMeta.className = 'hz-atc-sticky-bar__meta';
    bodyMeta.hidden = true;

    var bodyDetail = document.createElement('p');
    bodyDetail.className = 'hz-atc-sticky-bar__detail';

    var bodyDetailCopy = document.createElement('span');
    bodyDetailCopy.className = 'hz-atc-sticky-bar__detail-copy';

    var bodyCompare = document.createElement('span');
    bodyCompare.className = 'hz-atc-sticky-bar__compare';
    bodyCompare.hidden = true;

    var bodyPrice = document.createElement('span');
    bodyPrice.className = 'hz-atc-sticky-bar__price';

    bodyDetail.appendChild(bodyDetailCopy);
    bodyDetail.appendChild(bodyCompare);
    bodyDetail.appendChild(bodyPrice);
    bodyMeta.appendChild(bodyDetail);
    bodyBar.appendChild(bodyMeta);

    var originalParent = container.parentNode;
    var stickyActive = false;
    var stickyLeaving = false;
    var leaveTimer;

    function syncStickyQtyState() {
      var showQty = panel.getAttribute('data-hz-rc-show-qty') || 'false';
      bodyBar.setAttribute('data-hz-rc-show-qty', showQty);
    }

    function syncStickyMetrics() {
      if (!stickyActive) return;

      var rect = placeholder.getBoundingClientRect();
      bodyBar.style.setProperty('--hz-atc-sticky-left', rect.left + 'px');
      bodyBar.style.setProperty('--hz-atc-sticky-width', rect.width + 'px');
    }

    function activateSticky() {
      clearTimeout(leaveTimer);
      stickyLeaving = false;

      if (stickyActive && bodyBar.classList.contains('hz-atc-sticky-bar--active')) return;

      if (!stickyActive) {
        stickyActive = true;
        placeholder.style.height = container.offsetHeight + 'px';
        placeholder.hidden = false;
        bodyBar.insertBefore(container, bodyMeta);
      }

      container.classList.add('hz-atc--sticky');
      syncStickyQtyState();
      syncStickyMetrics();
      var host = panel.closest(HOST_SEL);
      if (host) {
        syncAddToCartLabel(host, container.querySelector('.product-form__submit.js-add-to-cart'));
        syncStickyAtcMeta(bodyBar, bodyMeta, bodyDetailCopy, bodyCompare, bodyPrice, host);
      }
      requestAnimationFrame(function () {
        bodyBar.classList.add('hz-atc-sticky-bar--active');
        if (host) {
          syncStickyAtcMeta(bodyBar, bodyMeta, bodyDetailCopy, bodyCompare, bodyPrice, host);
        }
      });
    }

    function finishDeactivate() {
      stickyActive = false;
      stickyLeaving = false;
      container.classList.remove('hz-atc--sticky');
      originalParent.insertBefore(container, placeholder.nextSibling);
      placeholder.hidden = true;
      placeholder.style.height = '';
      bodyBar.style.removeProperty('--hz-atc-sticky-left');
      bodyBar.style.removeProperty('--hz-atc-sticky-width');
      bodyMeta.hidden = true;
      var host = panel.closest(HOST_SEL);
      if (host) syncAddToCartLabel(host);
    }

    function deactivateSticky() {
      if (!stickyActive || stickyLeaving) return;
      bodyBar.classList.remove('hz-atc-sticky-bar--active');
      bodyMeta.hidden = true;
      clearTimeout(leaveTimer);

      if (!window.matchMedia('(max-width: 899px)').matches) {
        finishDeactivate();
        return;
      }

      // Mobile: return the buy row to its inline slot immediately on scroll reach.
      finishDeactivate();
    }

    function buyRowRect() {
      return (stickyActive || stickyLeaving ? placeholder : container).getBoundingClientRect();
    }

    function checkStickyState() {
      var headerBottom = stickyHeaderOffset();
      var rect = buyRowRect();
      var isMobile = window.matchMedia('(max-width: 899px)').matches;

      if (isMobile) {
        var viewportHeight = window.innerHeight;
        var buyRowFullyBelow = rect.top >= viewportHeight;
        var buyRowFullyAbove = rect.bottom <= 0;

        if (buyRowFullyBelow || buyRowFullyAbove) {
          activateSticky();
        } else {
          deactivateSticky();
        }
      } else {
        var triggerLine = headerBottom + stickySoonerOffset();
        var sentinelRect = sentinel.getBoundingClientRect();

        if (sentinelRect.top < triggerLine) {
          activateSticky();
        } else if (sentinelRect.top >= triggerLine) {
          deactivateSticky();
        }
      }

      syncStickyMetrics();
    }

    window.addEventListener('resize', checkStickyState, { passive: true });
    window.addEventListener('scroll', checkStickyState, { passive: true });

    var header = document.querySelector('.page-header');
    if (header) {
      var headerObserver = new MutationObserver(checkStickyState);
      headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
    }

    panel.addEventListener('hz-recharge:custom-selection-change', function () {
      syncStickyQtyState();
      var host = panel.closest(HOST_SEL);
      if (host) {
        syncAddToCartLabel(host);
        syncStickyAtcMeta(bodyBar, bodyMeta, bodyDetailCopy, bodyCompare, bodyPrice, host);
      }
    });
    var panelQtyObserver = new MutationObserver(syncStickyQtyState);
    panelQtyObserver.observe(panel, { attributes: true, attributeFilter: ['data-hz-rc-show-qty'] });

    document.addEventListener('hz-recharge:custom-ready', function (ev) {
      var host = panel.closest(HOST_SEL);
      if (!host || !host.contains(ev.target)) return;
      syncAddToCartLabel(host);
      syncStickyAtcMeta(bodyBar, bodyMeta, bodyDetailCopy, bodyCompare, bodyPrice, host);
    });

    checkStickyState();
    requestAnimationFrame(checkStickyState);
    window.addEventListener('load', checkStickyState, { once: true });
  }

  function initHost(host) {
    ensureSupplyPanelWithin(host);
    host.querySelectorAll('.recharge-supply-panel').forEach(function (panel) {
      bindQuantityInputRepairForPanel(panel);
      repairQuantityInputPlacement(panel);
      bindStickyAtcForPanel(panel);
    });
    syncAddToCartLabel(host);
  }

  function scan() {
    document.querySelectorAll(HOST_SEL).forEach(initHost);
  }

  var scanScheduled = false;
  function scheduleScan() {
    if (scanScheduled) return;
    scanScheduled = true;
    requestAnimationFrame(function () {
      scanScheduled = false;
      scan();
    });
  }

  document.addEventListener('hz-recharge:custom-ready', function (ev) {
    var host = ev.target && ev.target.closest(HOST_SEL);
    if (host) syncAddToCartLabel(host);
  });
  document.addEventListener('hz-recharge:custom-selection-change', function (ev) {
    var host = ev.target && ev.target.closest(HOST_SEL);
    if (host) syncAddToCartLabel(host);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scan);
  } else {
    scan();
  }

  var mo = new MutationObserver(scheduleScan);
  mo.observe(document.documentElement, { childList: true, subtree: true });
})();
